import React from "react";
import { Button, Card, ListGroup } from "react-bootstrap";
import { ExclamationTriangle, PlusCircleFill } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { AssignmentNotification, NotificationType } from "../../../../types/scheduling/meetings";
import {
  Congregation,
  PublicTalkAssignment,
  PublicTalkAssignmentInput,
  Speaker,
  WMSchedule,
} from "../../../../types/scheduling/weekend";
import { DateRange, NotificationDates } from "../../../../types/date";
import { CongSettings } from "../../../../types/scheduling/settings";
import { AssignmentSummary, useAssignmentMap } from "../../RecentAssignments";
import { EventAlert } from "../../eventAlert";
import { OutgoingAssignment } from "./OutgoingAssignment";
import { sortedWMCongregations } from "../../common";
import { weekendApi } from "../../../../api/weekend";
import { canUpdateWeekendSchedules } from "../wm_common";
import { Month } from "../../../../helpers/dateHelpers";

interface OutgoingSectionProps {
  week: WMSchedule;
  langGroupId: number;
  setOut: (out: PublicTalkAssignment) => void;
  setShowMoveModal: (show: boolean) => void;
  speakers: Speaker[];
  congregations: Congregation[];
  setSelectedID: (id: number) => void;
  setShowDeleteConfirm: (show: boolean) => void;
  notifications?: AssignmentNotification[];
  notificationDates: NotificationDates;
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  pwSchedules: any[];
  absences?: any[];
  refetchSchedules: () => void;
}

export const OutgoingSection: React.FC<OutgoingSectionProps> = (props) => {
  const { t } = useTranslation();
  const thisMonth = Month.fromDateString(props.week.date);
  const dateRange: DateRange = {
    from: thisMonth.start(),
    to: thisMonth.end(),
  };
  const assignmentMap = useAssignmentMap(dateRange, canUpdateWeekendSchedules(), props.langGroupId);

  // Check for notification mismatches (e.g. deleted or moved talks)
  const notificationMismatch = (): boolean => {
    let found = false;
    props.notifications
      ?.filter((n) => n.date === props.week.date)
      .forEach((n) => {
        if (n.type === NotificationType.PTOut && !props.week.out.some((w) => w.id === n.part)) found = true;
      });
    return found;
  };

  const assignments = props.week.out.map((out) => (
    <OutgoingAssignment
      key={out.id}
      out={out}
      langGroupId={props.langGroupId}
      setOut={props.setOut}
      setShowMoveModal={props.setShowMoveModal}
      speakers={props.speakers}
      congregations={props.congregations ? sortedWMCongregations(props.congregations) : []}
      setSelectedID={props.setSelectedID}
      setShowDeleteConfirm={props.setShowDeleteConfirm}
      notifications={props.notifications}
      notificationDates={props.notificationDates}
      congSettings={props.congSettings}
      userAssignmentMap={props.userAssignmentMap}
      pwSchedules={props.pwSchedules}
      absences={props.absences}
      assignmentMap={assignmentMap}
    />
  ));

  return (
    <Card className="p-3">
      <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
        {t("schedules.weekend.outgoing")}
        {notificationMismatch() && <ExclamationTriangle className="ms-2" />}
      </h5>

      {props.week.out.length === 0 ? (
        <>
          {props.week.event?.event === "mem" && <EventAlert context="wm" event={props.week.event} />}
          <p className="mb-3">{t("schedules.weekend.no-outgoing-talks")}</p>
        </>
      ) : (
        <ListGroup className="mb-3">{assignments}</ListGroup>
      )}
      <div>
        <Button
          variant="outline-primary"
          disabled={!canUpdateWeekendSchedules()}
          className="py-2"
          title={t("schedules.weekend.add-outgoing")}
          onClick={async () => {
            await weekendApi.setOutSchedule({ date: props.week.date } as PublicTalkAssignmentInput, props.langGroupId);
            props.refetchSchedules();
          }}
        >
          <PlusCircleFill className="me-1" size={18} />
          <span>{t("nav.publishers.add")}</span>
        </Button>
      </div>
    </Card>
  );
};
